import { useEffect, useState } from 'react';

import { ConfirmBox } from '@/components/confirm-box';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/**
 * THE REQUIRED-REASON DIALOG — ADM-02 §8's OTHER pattern: "an act that needs
 * a TYPED REASON gets its own dialog with a required text box instead."
 *
 * This is that dialog, and it is BUILT OVER the shared ConfirmBox shell
 * deliberately: §8's geometry (420px, top layer, dark blurred backdrop,
 * click-outside cancels, the verb never OK) lives in exactly one component,
 * and the required text box arrives here as caller-supplied BODY content —
 * ConfirmBox itself still has no text field, which is the line its own
 * header draws.
 *
 * Its callers are the GEN-72 posted-document cancels (GRN, delivery note,
 * bill, debit note — "cancelling a posted document requires a typed reason,
 * written to the audit log") and the BR-FIN-03-17 / BR-FIN-04-11 voucher
 * reversals, plus the bill's accept-variance reason (BR-PUR-03-06). All of
 * them used `window.prompt`, which is browser chrome — the same §8 breach
 * twice over as the `window.confirm` the attendance approval was caught
 * with: an OK button, and a box the product cannot style, place or
 * translate.
 *
 * The reason is REQUIRED: the confirm stays disabled until something is
 * typed, so Escape, click-outside or an empty answer abandons the act —
 * exactly the shape the old prompt enforced with `null`/empty checks.
 */
export function ReasonBox({
  open,
  title,
  prompt,
  reasonLabel,
  confirmLabel,
  onConfirm,
  onCancel,
  busy,
}: {
  open: boolean;
  title: string;
  /** The question, e.g. GEN-72's "Why is this … cancelled? …audit log." */
  prompt: string;
  /**
   * The text box's ACCESSIBLE NAME — distinct per flow ("Cancellation
   * reason", "Reversal reason"), never a bare "Reason": these editors
   * already carry Reason fields of their own, and two controls with one
   * name fail a screen-reader user before they fail a test (the masters
   * lesson).
   */
  reasonLabel: string;
  /** The verb, never OK — and never the trigger button's exact name. */
  confirmLabel: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const [reason, setReason] = useState('');
  // A fresh opening starts blank — a reason belongs to one act, not the next.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const fieldId = 'reason-box-input';
  return (
    <ConfirmBox
      open={open}
      title={title}
      body={
        <div className="grid gap-2">
          <p>{prompt}</p>
          <Label htmlFor={fieldId}>{reasonLabel}</Label>
          <Textarea
            id={fieldId}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            // The dialog exists to collect this one field, so focus lands in it.
            autoFocus
          />
        </div>
      }
      confirmLabel={confirmLabel}
      busy={busy === true || reason.trim() === ''}
      onConfirm={() => {
        if (reason.trim() !== '') onConfirm(reason.trim());
      }}
      onCancel={onCancel}
    />
  );
}
